import type { TabsListProps as MantineTabsListProps } from '@mantine/core';
import { Tabs as MantineTabs } from '@mantine/core';
import { forwardRef, memo, type ReactNode } from 'react';
import styled, { css } from 'styled-components';
import type { ITheme } from '../../utils/theme';
import Tab from './tab';
import { useScrollableTabs } from './use-scrollable-tabs';

const Tabs = styled(MantineTabs)`
  border-color: transparent;
  width: 100%;

  &:before {
    border-color: transparent;
  }
`;

const StyledTabsList = styled(MantineTabs.List)`
  ${({ theme }) => css`
    display: flex;
    flex-wrap: nowrap;
    scrollbar-width: none;
    overflow-x: auto;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    &:before {
      border-color: ${theme.palette.neutral[50]};
      width: 100%;
    }
  `}
`;

const RightSectionContainer = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;

interface TabsListProps extends MantineTabsListProps {
  rightSection?: ReactNode;
}

const List = forwardRef<HTMLDivElement, TabsListProps>(
  ({ children, rightSection, ...rest }, ref) => {
    const { tabListRef } = useScrollableTabs();

    // Merge the forwarded ref with the scrollable tab ref
    const mergedRef = (element: HTMLDivElement | null) => {
      tabListRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    return (
      <StyledTabsList {...rest} ref={mergedRef}>
        {children}
        {rightSection && (
          <RightSectionContainer aria-hidden="true">
            {rightSection}
          </RightSectionContainer>
        )}
      </StyledTabsList>
    );
  }
);

List.displayName = 'TabsList';

const Panel = styled(MantineTabs.Panel)`
  ${({ spacing, theme }: { spacing?: number; theme: ITheme }) => css`
    padding-top: ${theme.spacing(spacing || 5)};
  `}
`;

export default Object.assign(memo(Tabs), {
  Tab,
  List,
  Panel,
});
